/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { RcSlider } from 'common/components/RcSlider';
import { Icon } from '@pwc/pwc-deals-ui';
import styles from './styles.module.scss';

type TPropsRcSlider = React.ComponentProps<typeof RcSlider>;

const SVGScalingControls: React.FC<{
  onChangeValue?: (x: { readonly value: string }) => void;
  propsRoot?: React.HTMLAttributes<HTMLDivElement>;
  propsSlider?: TPropsRcSlider;
  valueSVGScale: string;
  isDarkMode?: boolean;
}> = ({
  onChangeValue,
  propsRoot,
  propsSlider,
  valueSVGScale,
  isDarkMode = false,
}) => {
  /* 
  We can send a default valueSVGScale with value 100 (for recently ingested SVGs where we don't have that value, so default could be 0 or 100)
  I decided to use 100 to fix these BUGS: 460972, 451364
  But sometimes happens that  valueSVGScale is bigger than the max possible size of the object (BUG 488168) and when clicking increase/decrease 
  button that would take the limit value the higher valueSVGScale so user wouldn't see that UI decreasing in the slider, so we need to check if
  we have both values make sure that our value is he max allowed.
  Adding this context fo future refactors for this to take a look into known bugs.
  */
  const [valueSlider, setValueSlider] = useState(() => {
    const parsedValueSVGScale = parseFloat(valueSVGScale);
    if (parsedValueSVGScale && propsSlider?.max) {
      return parsedValueSVGScale <= propsSlider?.max
        ? parsedValueSVGScale
        : propsSlider?.max;
    }

    return (
      parsedValueSVGScale || propsSlider?.defaultValue || propsSlider?.max || 0
    );
  });

  useEffect(() => {
    // Check if valueSlider has changed from the previous render
    if (valueSlider !== propsSlider?.defaultValue) {
      onChangeValue?.({ value: valueSlider?.toString() || '0' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueSlider]);

  const handleDecreaseClick = () => {
    const valueAdjustment = propsSlider?.max
      ? propsSlider.max * 0.05 // Decrease by 5% of the actual size
      : 1;
    const limit = propsSlider?.min || 0;

    let newValue: number;

    if (Array.isArray(valueSlider)) {
      newValue = valueSlider[0] - valueAdjustment;
    } else {
      newValue = valueSlider - valueAdjustment;
    }

    if (newValue >= limit) {
      setValueSlider(newValue);
    } else {
      setValueSlider(limit);
    }
  };

  const handleIncreaseClick = () => {
    const doesPropsSliderValueMaxExist = !!propsSlider?.max;
    let valueAdjustment = 0;
    if (propsSlider && propsSlider.max) {
      valueAdjustment = doesPropsSliderValueMaxExist
        ? propsSlider?.max * 0.05 // Increase by 5% of the actual size
        : 1;
    }
    const limit = doesPropsSliderValueMaxExist ? propsSlider?.max ?? 100 : 100;

    // Initialize newValue with valueSlider + valueAdjustment
    let newValue: number;
    if (Array.isArray(valueSlider)) {
      newValue = valueSlider[0] + valueAdjustment;
    } else {
      newValue = valueSlider + valueAdjustment;
    }

    // Check if the updated value is within the limit before updating state
    if (newValue <= limit) {
      setValueSlider(newValue);
    } else {
      setValueSlider(limit);
    }
  };

  const rootClassName = cn(styles.root, propsRoot?.className, {
    [styles.darkMode]: isDarkMode,
  });

  return (
    <div {...propsRoot} className={rootClassName}>
      {propsSlider && (
        <div className={styles.containerSlider}>
          <RcSlider
            data-testid="rc-slider"
            {...{
              activeDotStyle: {},
              dotStyle: {},
              handleStyle: {
                backgroundColor: 'var(--button-primary-color)',
                borderColor: 'var(--button-primary-color)',
                boxShadow: 'none',
                height: 16,
                marginTop: -7,
                opacity: 1,
                width: 16,
              },
              railStyle: { backgroundColor: '#BBB', height: 2 },
              trackStyle: {
                backgroundColor: 'var(--button-primary-color)',
                height: 2,
              },
              ...propsSlider,
            }}
            value={valueSlider}
            onChange={(value: number | number[]) => {
              setValueSlider(value);
            }}
          />
        </div>
      )}

      <div className={styles.containerSliderButtons}>
        <button
          type="button"
          className={styles.button}
          onClick={handleDecreaseClick}
          data-testid="slider-decrement" // Add data-testid to decrease button
        >
          <Icon name="minus" width="14" className="fill icon-gray" />
        </button>

        <button
          type="button"
          className={styles.button}
          onClick={handleIncreaseClick}
          data-testid="slider-increment" // Add data-testid to increase button
        >
          <Icon name="plus" width="14" className="fill icon-gray" />
        </button>
      </div>
    </div>
  );
};

export default SVGScalingControls;
